import {
	faCheck,
	faEyeSlash,
	faPlayCircle,
	faTimes,
} from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useState, MouseEvent } from "react";
import { FormattedMessage as String, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "@/hooks/useHistory";
import useSuraName from "@/hooks/useSuraName";
import { useAudio, useMessageBox } from "@/RefsProvider";
import { analytics } from "@/services/analytics";
import { getHifzRangeDisplayInfo } from "@/services/utils";
import {
	addHifzRange,
	deleteHifzRange,
	setRangeRevised,
} from "@/store/dbSlice";
import {
	gotoAya,
	gotoPage,
	hideMask,
	selectMaskOn,
	setSelectEnd,
	showMask,
} from "@/store/navSlice";
import { setReciteRange } from "@/store/playerSlice";
import { closePopupIfBlocking, showToast } from "@/store/uiSlice";
import {
	ayaID,
	ayaIdPage,
	getPageFirstAyaId,
	getRangeVerses,
	sura_info,
	verseLocation,
} from "@/services/qData";
import { SuraHifzChart } from "@/components/SuraHifzChart";
import { VerseText } from "@/components/Widgets";
import Icon from "@/components/Icon";

interface HifzRangeProps {
	range: HifzRange;
	filter?: string;
	showActions?: boolean;
	pages?: boolean;
	setActiveRange?: (rangeId: string | number) => void;
	trigger?: string;
}

export const HifzRange: React.FC<HifzRangeProps> = ({
	range,
	filter,
	showActions = false,
	pages = true,
	setActiveRange,
	trigger = "hifz_range",
}) => {
	const audio = useAudio();
	const msgBox = useMessageBox();
	const suraName = useSuraName(range.sura);
	const [rangeInfo, setRangeInfo] = useState("");
	const [ageInfo, setAgeInfo] = useState("");
	const [actions, setActions] = useState(showActions);
	const dispatch = useDispatch();
	const intl = useIntl();
	const history = useHistory();
	const maskOn = useSelector(selectMaskOn);

	const suraInfo = sura_info[range.sura];

	useEffect(() => {
		const { title, ageText } = getHifzRangeDisplayInfo(range, intl);
		setRangeInfo(title);
		setAgeInfo(ageText);
	}, [intl, range]);

	useEffect(() => {
		setActions(showActions);
	}, [showActions]);

	const rangeStartAya = (sura: number, page: number): number => {
		const suraStartPage = suraInfo.sp - 1;
		if (suraStartPage === page) {
			return ayaID(sura, 0);
		} else {
			return getPageFirstAyaId(page);
		}
	};

	const playRange = (_e: MouseEvent) => {
		// audio.stop();
		const [start, end] = getRangeVerses(
			range.sura,
			range.startPage,
			range.endPage
		);
		if (maskOn) {
			dispatch(hideMask());
		}
		dispatch(setReciteRange({ start, end }));
		audio?.play(start, false);
		dispatch(gotoPage(history, ayaIdPage(start)));
		dispatch(closePopupIfBlocking());
		analytics.logEvent("play_audio", {
			trigger,
			...verseLocation(start),
		});
	};

	const reviewRange = (_e: MouseEvent) => {
		const { startVerse } = selectRange();
		setTimeout(() => {
			dispatch(showMask());
			dispatch(closePopupIfBlocking());
		});
		analytics.logEvent("show_mask", {
			trigger,
			...verseLocation(startVerse),
		});
	};

	const selectRange = () => {
		const [startVerse, endVerse] = getRangeVerses(
			range.sura,
			range.startPage,
			range.endPage
		);
		dispatch(gotoAya(history, startVerse, { sel: true }));
		dispatch(setSelectEnd(endVerse)); //extend selection
		return { startVerse, endVerse };
	};

	const readRange = (_e: MouseEvent) => {
		const [rangeStartVerse] = getRangeVerses(
			range.sura,
			range.startPage,
			range.endPage
		);
		dispatch(gotoAya(history, rangeStartVerse, { sel: true }));

		dispatch(closePopupIfBlocking());
	};

	const onSetRangeRevised = (_e: MouseEvent) => {
		msgBox?.push({
			title: <String id="are_you_sure" />,
			onYes: () => {
				analytics.logEvent("revised_today", {
					trigger,
					chapter: range.sura,
					startPage: range.startPage,
					pagesCount: range.pages,
				});
				dispatch(setRangeRevised(range));
				dispatch(showToast({ id: "ack_range_revised" }));
			},
			content: <String id="revise_confirmation" />,
		});
	};

	const confirmAddHifz = (
		startPage: number,
		chapter: number,
		pagesCount: number,
		range: string
	) => {
		if (dispatch(addHifzRange(startPage, chapter, pagesCount))) {
			analytics.logEvent("add_hifz", {
				trigger,
				range,
				chapter,
				startPage,
				pagesCount,
			});
		} else {
			msgBox?.push({
				title: <String id="are_you_sure" />,
				onYes: () => {
					analytics.logEvent("add_hifz", {
						trigger,
						range,
						chapter,
						startPage,
						pagesCount,
					});
					dispatch(
						addHifzRange(
							startPage,
							chapter,
							pagesCount,
							true /*overwrite*/
						)
					);
				},
				content: <String id="overwrite_existing_hifz" />,
			});
		}
	};

	const addCurrentPage = (_e: MouseEvent) => {
		confirmAddHifz(range.startPage, range.sura, 1, "page");
	};

	const addSura = (_e: MouseEvent) => {
		const pagesCount = suraInfo.ep - suraInfo.sp + 1;
		confirmAddHifz(suraInfo.sp - 1, range.sura, pagesCount, "full_sura");
	};

	const addFromSuraStart = (_e: MouseEvent) => {
		const pagesCount = range.startPage - suraInfo.sp + 2;
		confirmAddHifz(
			suraInfo.sp - 1,
			range.sura,
			pagesCount,
			"from_sura_start"
		);
	};

	const addToSuraEnd = (_e: MouseEvent) => {
		const pagesCount = suraInfo.ep - range.startPage;
		confirmAddHifz(
			range.startPage,
			range.sura,
			pagesCount,
			"from_sura_start"
		);
	};

	const onDeleteHifzRange = (_e: MouseEvent) => {
		msgBox?.push({
			title: <String id="are_you_sure" />,
			onYes: () => {
				analytics.logEvent("remove_hifz", {
					chapter: range.sura,
					startPage: range.startPage,
					pagesCount: range.pages,
					trigger,
				});
				dispatch(deleteHifzRange(range));
			},
			content: <String id="remove_hifz" />,
		});
	};

	if (filter && suraName.match(new RegExp(filter, "i")) === null) {
		return "";
	}

	const toggleActions = (e: MouseEvent) => {
		if (showActions) {
			readRange(e);
			return;
		}
		setActiveRange?.(range.id);
	};

	return (
		<li className={"HifzRangeRow"}>
			<SuraHifzChart
				sura={range.sura}
				pages={pages}
				range={range}
				trigger="update_hifz_popup"
			/>
			<div
				className="HifzRangeBody"
				tabIndex={0}
				onClick={toggleActions}
				style={{
					textAlign: "inherit",
				}}
			>
				<div className="RangeInfo">
					{suraName}
					{": "}
					{rangeInfo}
				</div>
				<div className="AgeInfo">{ageInfo}</div>
				<div
					className="RangeText"
					style={{
						whiteSpace: "nowrap",
						pointerEvents: "none",
					}}
				>
					{!actions ? (
						<VerseText
							verse={rangeStartAya(range.sura, range.startPage)}
						/>
					) : (
						""
					)}
				</div>
			</div>
			{range.date ? (
				actions ? (
					<div className="ActionsBar">
						<button
							onClick={playRange}
							title={intl.formatMessage({ id: "play" })}
						>
							<Icon icon={faPlayCircle} />
						</button>
						<button
							onClick={reviewRange}
							title={intl.formatMessage({ id: "revise" })}
						>
							<Icon icon={faEyeSlash} />
						</button>
						<button onClick={onSetRangeRevised}>
							<Icon icon={faCheck} /> <String id="revised" />
						</button>
						{/* <button onClick={readRange}>
													<Icon icon={faBookOpen} />
											</button> */}
						<button
							onClick={onDeleteHifzRange}
							title={intl.formatMessage({ id: "remove_hifz" })}
						>
							<Icon icon={faTimes} />
						</button>
					</div>
				) : (
					""
				)
			) : (
				<div className="ActionsBar">
					<String id="add" />
					<button onClick={addCurrentPage}>
						<String id="the_page" />
					</button>
					<button onClick={addSura}>
						<String id="the_sura" />
					</button>
					<button onClick={addFromSuraStart}>
						<String id="from_start" />
					</button>
					<button onClick={addToSuraEnd}>
						<String id="to_end" />
					</button>
				</div>
			)}
		</li>
	);
};
